/* eslint-disable import/prefer-default-export */
import React, { useEffect } from 'react';
import { Form, Radio, Button, Icon } from 'antd';
import {
  OTP_TYPE,
  MICROSOFT_AUTHENTICATOR_LABEL,
  GOOGLE_AUTHENTICATOR_LABEL,
  isSmsSecondFactorOptionAvailable,
  normalizeOtpTypeForPartnerUi,
  isAuthenticatorOtpType,
  getAuthenticatorAppLabel,
} from '../../../../../constants/mfa';

const FormItem = Form.Item;

function OtpRadioCard({ title, help, value, disabled }) {
  return (
    <Radio value={value} disabled={disabled}>
      <span className="user-edit-otp-option">
        <span className="user-edit-otp-title">{title}</span>
        <span className="user-edit-otp-help">{help}</span>
      </span>
    </Radio>
  );
}

function AuthenticatorSetupPanel({ appLabel, userRequest, onOpenEnrollment }) {
  return (
    <div className="user-edit-mfa-panel">
      <div className="user-edit-mfa-panel-head">
        <Icon type="mobile" className="user-edit-mfa-panel-icon" />
        <div className="user-edit-mfa-panel-text">
          <div className="user-edit-mfa-panel-title">Finish authenticator setup</div>
          <p className="user-edit-mfa-panel-desc">
            Add this account in {appLabel}, then save your profile before you can use this option to sign in.
          </p>
        </div>
      </div>
      <Button type="primary" block disabled={userRequest} onClick={onOpenEnrollment}>
        {`Set up ${appLabel}`}
      </Button>
    </div>
  );
}

function AuthenticatorActivePanel({ appLabel, userRequest, onRemove }) {
  return (
    <div className="user-edit-mfa-panel user-edit-mfa-panel--active">
      <div>
        <span className="user-edit-mfa-status">
          <Icon type="check-circle" theme="filled" />
          {`${appLabel} is active`}
        </span>
        <p className="user-edit-mfa-panel-desc user-edit-mfa-panel-desc--flush">
          {`Remove ${appLabel} before you can choose another sign-in option.`}
        </p>
      </div>
      <Button type="danger" ghost size="small" disabled={userRequest} onClick={onRemove}>
        Remove app
      </Button>
    </div>
  );
}

/**
 * Second sign-in factor: email / SMS / authenticator app.
 */
export function UserEditSecondFactorSection({
  form,
  userInfo,
  userRequest,
  phoneNumber,
  requireMobileVerification,
  onOtpTypeChange,
  onOpenEnrollment,
  onConfirmRemoveAuthenticator,
  getFieldDecorator,
}) {
  const showSmsOption = isSmsSecondFactorOptionAvailable();
  const hasPhone = Boolean(phoneNumber || userInfo.phoneNumber);
  const selectedOtpType = form.getFieldValue('otpType');
  const activeOtpType = userInfo.otpType;
  const authenticatorIsActive = isAuthenticatorOtpType(activeOtpType);
  const showSetupPanel = isAuthenticatorOtpType(selectedOtpType) && !authenticatorIsActive;
  const showActivePanel = authenticatorIsActive;
  const setupLabel = getAuthenticatorAppLabel(selectedOtpType);
  const activeLabel = getAuthenticatorAppLabel(activeOtpType);

  useEffect(() => {
    if (!showSmsOption && form.getFieldValue('otpType') === OTP_TYPE.SMS) {
      form.setFieldsValue({ otpType: OTP_TYPE.EMAIL });
    }
  }, [form, showSmsOption, userInfo.id, userInfo.otpType]);

  return (
    <div className="user-edit-2fa-section">
      <div className="user-edit-2fa-heading">2FA settings</div>
      <p className="user-edit-2fa-lead">Choose how you want to verify it&apos;s you when you log in.</p>

      <FormItem label={null} colon={false} className="user-edit-2fa-radios-wrap">
        {getFieldDecorator('otpType', {
          initialValue: normalizeOtpTypeForPartnerUi(userInfo.otpType) ?? OTP_TYPE.EMAIL,
        })(
          <Radio.Group className="user-edit-otp-radios" disabled={userRequest} onChange={onOtpTypeChange}>
            <OtpRadioCard
              value={OTP_TYPE.EMAIL}
              title="Email"
              help="One-time code sent to your inbox"
              disabled={authenticatorIsActive}
            />
            {showSmsOption && (
              <OtpRadioCard
                value={OTP_TYPE.SMS}
                disabled={!hasPhone || requireMobileVerification || authenticatorIsActive}
                title="SMS"
                help={
                  hasPhone
                    ? 'Text message to your verified mobile number'
                    : 'Add a phone number above to use SMS'
                }
              />
            )}
            <OtpRadioCard
              value={OTP_TYPE.AUTHENTICATOR}
              title={MICROSOFT_AUTHENTICATOR_LABEL}
              help="Codes from the Microsoft Authenticator app"
              disabled={authenticatorIsActive && activeOtpType !== OTP_TYPE.AUTHENTICATOR}
            />
            <OtpRadioCard
              value={OTP_TYPE.GOOGLE_AUTHENTICATOR}
              title={GOOGLE_AUTHENTICATOR_LABEL}
              help="Codes from the Google Authenticator app"
              disabled={authenticatorIsActive && activeOtpType !== OTP_TYPE.GOOGLE_AUTHENTICATOR}
            />
          </Radio.Group>,
        )}
      </FormItem>

      {showSetupPanel && (
        <AuthenticatorSetupPanel appLabel={setupLabel} userRequest={userRequest} onOpenEnrollment={onOpenEnrollment} />
      )}

      {showActivePanel && (
        <AuthenticatorActivePanel
          appLabel={activeLabel}
          userRequest={userRequest}
          onRemove={() => onConfirmRemoveAuthenticator(OTP_TYPE.EMAIL)}
        />
      )}
    </div>
  );
}
